import { useState, useMemo, useCallback, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { v4 as uuid } from 'uuid';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
    MdAdd as AddIcon,
    MdDelete as DeleteIcon,
    MdArrowUpward as UpIcon,
    MdArrowDownward as DownIcon,
    MdGridView as TemplatesIcon,
    MdClose as CloseIcon,
    MdExpandMore as ExpandMoreIcon,
    MdExpandLess as ExpandLessIcon,
    MdPreview as PreviewIcon,
} from 'react-icons/md';
import { IoMailOutline as MailIcon } from 'react-icons/io5';
import './EmailDesigner.scss';

const cn = 'emailDesigner';

// ── Block defaults ──────────────────────────────────────────────────────────
const BLOCK_DEFAULTS = {
    header: {
        logoText: 'KZQ RESOLVE',
        title: 'Notification',
        subtitle: '',
        bgColor: '#2784B9',
        titleColor: '#ffffff',
        paddingTop: 32, paddingBottom: 32, paddingLeft: 32, paddingRight: 32,
    },
    text: {
        html: '<p>Enter your message here...</p>',
        bgColor: '#ffffff',
        paddingTop: 20, paddingBottom: 20, paddingLeft: 28, paddingRight: 28,
    },
    button: {
        text: 'View Ticket',
        url: '{{ticketURL}}',
        bgColor: '#2784B9',
        color: '#ffffff',
        borderRadius: 6,
        align: 'center',
        containerBg: '#ffffff',
        paddingTop: 20, paddingBottom: 8, paddingLeft: 28, paddingRight: 28,
    },
    divider: {
        borderColor: '#e8eaed',
        bgColor: '#ffffff',
        paddingTop: 8, paddingBottom: 8, paddingLeft: 28, paddingRight: 28,
    },
    spacer: {
        height: 24,
        bgColor: '#ffffff',
    },
    footer: {
        html: '<p style="color:#9aa0a6;font-size:12px;text-align:center;margin:0">This email was sent by KZQ Resolve &nbsp;·&nbsp; <a href="{{ticketURL}}" style="color:#9aa0a6">View Ticket</a></p>',
        bgColor: '#f8f9fa',
        paddingTop: 24, paddingBottom: 24, paddingLeft: 28, paddingRight: 28,
    },
};

const createBlock = (type, overrides = {}) => ({
    id: uuid(),
    type,
    ...BLOCK_DEFAULTS[type],
    ...overrides,
});

// ── Export blocks → email-safe HTML ─────────────────────────────────────────
const exportHtml = (blocks, canvasBg) => {
    const rows = blocks.map(b => {
        const pad = `padding:${b.paddingTop ?? 16}px ${b.paddingRight ?? 24}px ${b.paddingBottom ?? 16}px ${b.paddingLeft ?? 24}px`;

        if (b.type === 'header') {
            return `<tr><td style="background:${b.bgColor};${pad};text-align:center;font-family:Arial,sans-serif">
  <div style="font-size:10px;font-weight:700;color:rgba(255,255,255,0.65);letter-spacing:2px;text-transform:uppercase;margin-bottom:10px">${b.logoText}</div>
  <div style="font-size:24px;font-weight:700;color:${b.titleColor};line-height:1.2;margin-bottom:${b.subtitle ? '8px' : '0'}">${b.title}</div>
  ${b.subtitle ? `<div style="font-size:14px;color:rgba(255,255,255,0.82);margin:0">${b.subtitle}</div>` : ''}
</td></tr>`;
        }
        if (b.type === 'text' || b.type === 'footer') {
            return `<tr><td style="background:${b.bgColor};${pad};font-family:Arial,sans-serif;font-size:14px;color:#333333;line-height:1.6">${b.html}</td></tr>`;
        }
        if (b.type === 'button') {
            return `<tr><td style="background:${b.containerBg};${pad};text-align:${b.align}">
  <a href="${b.url}" style="display:inline-block;background:${b.bgColor};color:${b.color};font-family:Arial,sans-serif;font-size:15px;font-weight:700;text-decoration:none;padding:13px 30px;border-radius:${b.borderRadius}px">${b.text}</a>
</td></tr>`;
        }
        if (b.type === 'divider') {
            return `<tr><td style="background:${b.bgColor};${pad}"><hr style="border:none;border-top:1px solid ${b.borderColor};margin:0"/></td></tr>`;
        }
        if (b.type === 'spacer') {
            return `<tr><td style="background:${b.bgColor};height:${b.height}px;font-size:1px;line-height:1px">&nbsp;</td></tr>`;
        }
        return '';
    }).join('\n');

    const meta = `<!-- emailDesignerData:${btoa(unescape(encodeURIComponent(JSON.stringify({ blocks, canvasBg }))))} -->`;

    return `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
</head>
<body style="margin:0;padding:0;background-color:${canvasBg}">
<table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:${canvasBg}">
<tr><td align="center" style="padding:24px 0">
<table width="600" cellpadding="0" cellspacing="0" border="0" style="max-width:600px;width:100%;background:#ffffff;border-radius:8px;overflow:hidden">
${rows}
</table>
</td></tr>
</table>
${meta}
</body>
</html>`;
};

const parseDesignerData = (html) => {
    if (!html) return null;
    const m = html.match(/<!-- emailDesignerData:([A-Za-z0-9+/=]+) -->/);
    if (!m) return null;
    try { return JSON.parse(decodeURIComponent(escape(atob(m[1])))); } catch { return null; }
};

// ── Shared info table helper ─────────────────────────────────────────────────
const infoTable = (rows, bgColor = '#f0f7ff') => ({
    bgColor,
    html: `<table width="100%" cellpadding="0" cellspacing="0" style="font-family:Arial,sans-serif;font-size:13px">
${rows.map(([label, value, color]) =>
    `<tr><td style="padding:5px 0;color:#718096;width:140px">${label}</td><td style="padding:5px 0;font-weight:700;color:${color || '#2d3748'}">${value}</td></tr>`
).join('\n')}
</table>`,
});

// ── Template library ──────────────────────────────────────────────────────────
export const TEMPLATES = [

    // ════════════════════════════════════════════════════════════════════════
    //  CREATE
    // ════════════════════════════════════════════════════════════════════════
    {
        key: 'create_professional',
        channelType: 'create',
        label: 'Professional',
        tone: 'Corporate',
        accent: '#2784B9',
        desc: 'Clean blue design with a ticket details table. Great for formal support teams.',
        make: () => [
            createBlock('header', { title: 'Ticket Received', subtitle: 'Your request has been logged', bgColor: '#2784B9' }),
            createBlock('text', { html: '<p style="margin:0 0 12px">Dear <strong>{{toUserFullName}}</strong>,</p><p style="margin:0">Thank you for reaching out. Your support ticket has been successfully created and assigned a unique reference number. Our team will review your request and respond as soon as possible.</p>' }),
            createBlock('divider', {}),
            createBlock('text', {
                ...infoTable([
                    ['Ticket ID', '{{ticketId}}'],
                    ['Form', '{{formTitle}}'],
                    ['Status', '{{ticketStatus}}'],
                ], '#f0f7ff'),
                paddingTop: 16, paddingBottom: 16, paddingLeft: 28, paddingRight: 28,
            }),
            createBlock('button', { text: 'Track Your Ticket', url: '{{ticketURL}}', bgColor: '#2784B9', paddingTop: 24, paddingBottom: 8 }),
            createBlock('text', { html: '<p style="margin:0;color:#718096;font-size:13px">Questions? Simply reply to this email and we\'ll be happy to help.</p>' }),
            createBlock('divider', {}),
            createBlock('footer', {}),
        ],
    },

    {
        key: 'create_friendly',
        channelType: 'create',
        label: 'Friendly',
        tone: 'Warm & Casual',
        accent: '#0891b2',
        desc: 'Approachable tone with a welcoming message. Perfect for customer-facing teams.',
        make: () => [
            createBlock('header', { title: 'Got it! ✓', subtitle: 'Your ticket is in safe hands', bgColor: '#0891b2', paddingTop: 36, paddingBottom: 36 }),
            createBlock('text', { html: '<p style="margin:0 0 14px">Hey <strong>{{toUserFullName}}</strong>! 👋</p><p style="margin:0 0 12px">We\'ve received your request and we\'re on it! You can relax — our team will take it from here and keep you updated every step of the way.</p><p style="margin:0">We\'ll aim to get back to you as soon as we can.</p>' }),
            createBlock('text', {
                bgColor: '#ecfeff',
                html: '<p style="margin:0;font-size:13px;color:#0e7490">Your ticket reference is <strong style="font-size:16px;color:#0891b2">{{ticketId}}</strong> — keep this handy!</p>',
                paddingTop: 14, paddingBottom: 14, paddingLeft: 20, paddingRight: 20,
            }),
            createBlock('button', { text: 'See Your Ticket', url: '{{ticketURL}}', bgColor: '#0891b2', borderRadius: 24, paddingTop: 20, paddingBottom: 12 }),
            createBlock('text', { html: '<p style="margin:0;color:#718096;font-size:13px;text-align:center">We\'re always happy to help. Reply to this email anytime.</p>' }),
            createBlock('divider', {}),
            createBlock('footer', { bgColor: '#f0fdff' }),
        ],
    },

    {
        key: 'create_minimal',
        channelType: 'create',
        label: 'Minimal',
        tone: 'Clean & Simple',
        accent: '#374151',
        desc: 'No-frills design with just the essentials. Ideal for internal tooling.',
        make: () => [
            createBlock('header', { logoText: 'KZQ RESOLVE', title: 'Ticket Created', subtitle: '', bgColor: '#1f2937', paddingTop: 28, paddingBottom: 28 }),
            createBlock('spacer', { height: 8, bgColor: '#ffffff' }),
            createBlock('text', { html: '<p style="margin:0 0 16px;color:#374151">Hello <strong>{{toUserFullName}}</strong>,</p><p style="margin:0;color:#6b7280;line-height:1.7">Your ticket has been received. We\'ll review it shortly and follow up by email. You can check the current status at any time using the link below.</p>' }),
            createBlock('text', {
                bgColor: '#f9fafb',
                html: `<table width="100%" cellpadding="0" cellspacing="0" style="font-family:Arial,sans-serif;font-size:13px;border-collapse:collapse">
<tr><td style="padding:8px 0;color:#6b7280;border-bottom:1px solid #e5e7eb;width:130px">Reference</td><td style="padding:8px 0;font-weight:700;color:#111827;border-bottom:1px solid #e5e7eb">{{ticketId}}</td></tr>
<tr><td style="padding:8px 0;color:#6b7280;border-bottom:1px solid #e5e7eb">Form</td><td style="padding:8px 0;color:#374151;border-bottom:1px solid #e5e7eb">{{formTitle}}</td></tr>
<tr><td style="padding:8px 0;color:#6b7280">Status</td><td style="padding:8px 0;color:#374151">{{ticketStatus}}</td></tr>
</table>`,
                paddingTop: 16, paddingBottom: 8, paddingLeft: 20, paddingRight: 20,
            }),
            createBlock('button', { text: 'View Ticket', url: '{{ticketURL}}', bgColor: '#1f2937', color: '#ffffff', borderRadius: 4, paddingTop: 20, paddingBottom: 8 }),
            createBlock('divider', { borderColor: '#e5e7eb' }),
            createBlock('footer', { bgColor: '#f9fafb', html: '<p style="color:#9ca3af;font-size:11px;text-align:center;margin:0">KZQ Resolve · This is an automated notification</p>' }),
        ],
    },

    {
        key: 'create_enterprise',
        channelType: 'create',
        label: 'Enterprise',
        tone: 'Structured',
        accent: '#1e3a5f',
        desc: 'Dark navy header with a structured layout. Built for high-volume support.',
        make: () => [
            createBlock('header', { logoText: 'SUPPORT PORTAL', title: 'Support Request Received', subtitle: 'Reference: {{ticketId}}', bgColor: '#1e3a5f', paddingTop: 36, paddingBottom: 36 }),
            createBlock('text', { html: '<p style="margin:0 0 12px">Dear <strong>{{toUserFullName}}</strong>,</p><p style="margin:0">We confirm receipt of your support request. A member of our team has been notified and will assess your case in accordance with our standard response times. Please retain your ticket reference for all future correspondence.</p>' }),
            createBlock('text', {
                bgColor: '#f1f5f9',
                html: `<table width="100%" cellpadding="0" cellspacing="0" style="font-family:Arial,sans-serif;font-size:13px">
<tr><td style="padding:6px 0;color:#64748b;width:140px">Reference Number</td><td style="padding:6px 0;font-weight:700;color:#1e293b;font-size:15px">{{ticketId}}</td></tr>
<tr><td style="padding:6px 0;color:#64748b">Submitted Via</td><td style="padding:6px 0;color:#334155">{{formTitle}}</td></tr>
<tr><td style="padding:6px 0;color:#64748b">Initial Status</td><td style="padding:6px 0;color:#334155">{{ticketStatus}}</td></tr>
<tr><td style="padding:6px 0;color:#64748b">Submitted By</td><td style="padding:6px 0;color:#334155">{{toUserFullName}} ({{toUserEmail}})</td></tr>
</table>`,
                paddingTop: 16, paddingBottom: 16, paddingLeft: 20, paddingRight: 20,
            }),
            createBlock('text', { html: '<p style="margin:0;color:#64748b;font-size:13px;line-height:1.6">To view updates, upload attachments, or add additional information to your case, please use the secure ticket portal link below.</p>' }),
            createBlock('button', { text: 'Access Ticket Portal', url: '{{ticketURL}}', bgColor: '#1e3a5f', color: '#ffffff', borderRadius: 4, paddingTop: 20, paddingBottom: 8 }),
            createBlock('divider', { borderColor: '#e2e8f0' }),
            createBlock('footer', { bgColor: '#f8fafc', html: '<p style="color:#94a3b8;font-size:11px;text-align:center;margin:0">This is a system-generated notification. Do not reply directly to this email.<br>For assistance, visit your ticket portal or contact support.</p>' }),
        ],
    },

    // ════════════════════════════════════════════════════════════════════════
    //  UPDATE
    // ════════════════════════════════════════════════════════════════════════
    {
        key: 'update_standard',
        channelType: 'update',
        label: 'Standard Update',
        tone: 'Informative',
        accent: '#d97706',
        desc: 'Amber design with a status table. Clear and informative update notification.',
        make: () => [
            createBlock('header', { title: 'Ticket Updated', subtitle: 'There\'s new activity on your request', bgColor: '#d97706' }),
            createBlock('text', { html: '<p style="margin:0 0 12px">Hi <strong>{{toUserFullName}}</strong>,</p><p style="margin:0">Your ticket has been updated. Please review the latest information below and let us know if you have any questions or need further assistance.</p>' }),
            createBlock('divider', {}),
            createBlock('text', {
                ...infoTable([
                    ['Ticket ID', '{{ticketId}}'],
                    ['Current Status', '{{ticketStatus}}', '#d97706'],
                    ['Form', '{{formTitle}}'],
                ], '#fffbeb'),
                paddingTop: 16, paddingBottom: 16, paddingLeft: 28, paddingRight: 28,
            }),
            createBlock('button', { text: 'View Latest Update', url: '{{ticketURL}}', bgColor: '#d97706', paddingTop: 24, paddingBottom: 8 }),
            createBlock('text', { html: '<p style="margin:0;color:#718096;font-size:13px">Need to add information? Reply to this email or visit the ticket portal.</p>' }),
            createBlock('divider', {}),
            createBlock('footer', {}),
        ],
    },

    {
        key: 'update_status_change',
        channelType: 'update',
        label: 'Status Change',
        tone: 'Focused',
        accent: '#7c3aed',
        desc: 'Highlights the new status prominently. Best when the key news is a status change.',
        make: () => [
            createBlock('header', { title: 'Status Update', subtitle: 'Your ticket status has changed', bgColor: '#7c3aed' }),
            createBlock('text', { html: '<p style="margin:0">Hi <strong>{{toUserFullName}}</strong>,</p>' }),
            createBlock('text', {
                bgColor: '#f5f3ff',
                html: `<p style="margin:0;text-align:center;font-family:Arial,sans-serif">
  <span style="font-size:12px;color:#7c3aed;text-transform:uppercase;letter-spacing:1px;font-weight:700">New Status</span><br>
  <span style="font-size:28px;font-weight:700;color:#5b21b6">{{ticketStatus}}</span>
</p>`,
                paddingTop: 24, paddingBottom: 24, paddingLeft: 28, paddingRight: 28,
            }),
            createBlock('text', { html: '<p style="margin:0;color:#4b5563;line-height:1.7">Your ticket <strong>{{ticketId}}</strong> has been updated. If you have any questions about this change or need to provide additional information, please visit your ticket using the button below.</p>' }),
            createBlock('button', { text: 'See Full Ticket Details', url: '{{ticketURL}}', bgColor: '#7c3aed', borderRadius: 8, paddingTop: 20, paddingBottom: 8 }),
            createBlock('divider', {}),
            createBlock('footer', {}),
        ],
    },

    {
        key: 'update_action_required',
        channelType: 'update',
        label: 'Action Required',
        tone: 'Urgent',
        accent: '#dc2626',
        desc: 'Red accent with a clear call to action. Use when a response is needed from the submitter.',
        make: () => [
            createBlock('header', { title: 'Action Required', subtitle: 'Your input is needed to proceed', bgColor: '#dc2626' }),
            createBlock('text', { html: '<p style="margin:0 0 12px">Hi <strong>{{toUserFullName}}</strong>,</p><p style="margin:0">We have reviewed your ticket and need some additional information from you before we can continue. Please visit your ticket as soon as possible and provide the requested details.</p>' }),
            createBlock('text', {
                bgColor: '#fff1f2',
                html: '<p style="margin:0;font-size:13px;color:#dc2626;font-weight:600">⚠ Without your input, we may not be able to resolve this ticket.</p>',
                paddingTop: 14, paddingBottom: 14, paddingLeft: 20, paddingRight: 20,
            }),
            createBlock('text', {
                ...infoTable([
                    ['Ticket ID', '{{ticketId}}'],
                    ['Status', '{{ticketStatus}}', '#dc2626'],
                ], '#fef2f2'),
                paddingTop: 12, paddingBottom: 12, paddingLeft: 28, paddingRight: 28,
            }),
            createBlock('button', { text: 'Respond Now', url: '{{ticketURL}}', bgColor: '#dc2626', borderRadius: 6, paddingTop: 20, paddingBottom: 8 }),
            createBlock('text', { html: '<p style="margin:0;color:#718096;font-size:13px">If you have already responded, please disregard this email.</p>' }),
            createBlock('divider', {}),
            createBlock('footer', {}),
        ],
    },

    {
        key: 'update_friendly',
        channelType: 'update',
        label: 'Friendly Update',
        tone: 'Warm',
        accent: '#2563eb',
        desc: 'Casual, friendly tone. Good for teams that want to sound approachable.',
        make: () => [
            createBlock('header', { title: 'Quick Update!', subtitle: 'We\'ve been working on your ticket', bgColor: '#2563eb', paddingTop: 36, paddingBottom: 36 }),
            createBlock('text', { html: '<p style="margin:0 0 14px">Hey <strong>{{toUserFullName}}</strong>! 👋</p><p style="margin:0 0 12px">We wanted to let you know there\'s been an update on your ticket <strong>{{ticketId}}</strong>. Things are moving along — check the portal to see what\'s changed.</p><p style="margin:0">As always, feel free to reach out if you have any questions!</p>' }),
            createBlock('button', { text: 'See What\'s New', url: '{{ticketURL}}', bgColor: '#2563eb', borderRadius: 24, paddingTop: 24, paddingBottom: 12 }),
            createBlock('text', { html: '<p style="margin:0;color:#718096;font-size:13px;text-align:center">Still have questions? Just reply to this email — we\'re here to help.</p>' }),
            createBlock('divider', {}),
            createBlock('footer', { bgColor: '#eff6ff' }),
        ],
    },

    // ════════════════════════════════════════════════════════════════════════
    //  RESOLVE
    // ════════════════════════════════════════════════════════════════════════
    {
        key: 'resolve_professional',
        channelType: 'resolve',
        label: 'Professional',
        tone: 'Formal',
        accent: '#059669',
        desc: 'Classic green resolution email. Clean and professional with a full summary.',
        make: () => [
            createBlock('header', { title: 'Ticket Resolved', subtitle: 'Your request has been completed', bgColor: '#059669' }),
            createBlock('text', { html: '<p style="margin:0 0 12px">Dear <strong>{{toUserFullName}}</strong>,</p><p style="margin:0">We are pleased to inform you that your support ticket has been resolved. Please find the resolution summary below. If you believe the issue has not been fully addressed, you can reopen your ticket at any time.</p>' }),
            createBlock('divider', {}),
            createBlock('text', {
                ...infoTable([
                    ['Ticket ID', '{{ticketId}}'],
                    ['Resolution', 'Resolved', '#059669'],
                    ['Form', '{{formTitle}}'],
                    ['Contact', '{{toUserEmail}}'],
                ], '#ecfdf5'),
                paddingTop: 16, paddingBottom: 16, paddingLeft: 28, paddingRight: 28,
            }),
            createBlock('button', { text: 'View Resolution', url: '{{ticketURL}}', bgColor: '#059669', paddingTop: 24, paddingBottom: 8 }),
            createBlock('text', { html: '<p style="margin:0;color:#718096;font-size:13px">Thank you for giving us the opportunity to assist you. We hope the resolution meets your expectations.</p>' }),
            createBlock('divider', {}),
            createBlock('footer', {}),
        ],
    },

    {
        key: 'resolve_celebration',
        channelType: 'resolve',
        label: 'Great News!',
        tone: 'Celebratory',
        accent: '#10b981',
        desc: 'Upbeat, positive tone. Makes resolving tickets feel like a win for the customer.',
        make: () => [
            createBlock('header', { title: 'Great News! 🎉', subtitle: 'Your issue has been resolved', bgColor: '#10b981', paddingTop: 40, paddingBottom: 40 }),
            createBlock('text', { html: '<p style="margin:0 0 14px">Hi <strong>{{toUserFullName}}</strong>! 😊</p><p style="margin:0 0 12px">We\'re thrilled to let you know that your ticket has been resolved! Our team worked hard to get this sorted for you, and we hope everything is back to normal.</p><p style="margin:0">If everything looks good on your end, no further action is needed.</p>' }),
            createBlock('text', {
                bgColor: '#f0fdf4',
                html: '<p style="margin:0;text-align:center;color:#059669;font-size:13px">Your ticket <strong>{{ticketId}}</strong> is now marked as <strong>Resolved</strong> ✓</p>',
                paddingTop: 16, paddingBottom: 16, paddingLeft: 20, paddingRight: 20,
            }),
            createBlock('button', { text: 'View Your Ticket', url: '{{ticketURL}}', bgColor: '#10b981', borderRadius: 24, paddingTop: 20, paddingBottom: 8 }),
            createBlock('text', { html: '<p style="margin:0;color:#718096;font-size:13px;text-align:center">Something still not right? You can reopen your ticket from the portal anytime.</p>' }),
            createBlock('divider', {}),
            createBlock('footer', { bgColor: '#f0fdf4' }),
        ],
    },

    {
        key: 'resolve_minimal',
        channelType: 'resolve',
        label: 'Minimal',
        tone: 'Clean & Direct',
        accent: '#374151',
        desc: 'No-frills confirmation. Just the facts — ticket closed, here\'s the link.',
        make: () => [
            createBlock('header', { logoText: 'KZQ RESOLVE', title: 'Ticket Closed', subtitle: '', bgColor: '#1f2937', paddingTop: 28, paddingBottom: 28 }),
            createBlock('spacer', { height: 8, bgColor: '#ffffff' }),
            createBlock('text', { html: '<p style="margin:0 0 16px;color:#374151">Hi <strong>{{toUserFullName}}</strong>,</p><p style="margin:0;color:#6b7280;line-height:1.7">Your ticket <strong>{{ticketId}}</strong> has been resolved and closed. You can view the full details using the link below. If you need to follow up, simply reopen your ticket from the portal.</p>' }),
            createBlock('button', { text: 'View Ticket', url: '{{ticketURL}}', bgColor: '#1f2937', color: '#ffffff', borderRadius: 4, paddingTop: 20, paddingBottom: 8 }),
            createBlock('divider', { borderColor: '#e5e7eb' }),
            createBlock('footer', { bgColor: '#f9fafb', html: '<p style="color:#9ca3af;font-size:11px;text-align:center;margin:0">KZQ Resolve · Automated notification · <a href="{{ticketURL}}" style="color:#9ca3af">View Ticket</a></p>' }),
        ],
    },

    {
        key: 'resolve_feedback',
        channelType: 'resolve',
        label: 'With Feedback',
        tone: 'Engaging',
        accent: '#7c3aed',
        desc: 'Includes a thank-you message and invites the customer to share feedback.',
        make: () => [
            createBlock('header', { title: 'Issue Resolved ✓', subtitle: 'We\'d love to hear how we did', bgColor: '#7c3aed', paddingTop: 36, paddingBottom: 36 }),
            createBlock('text', { html: '<p style="margin:0 0 12px">Hi <strong>{{toUserFullName}}</strong>,</p><p style="margin:0">Your ticket <strong>{{ticketId}}</strong> has been resolved. We hope we were able to help you effectively. Your experience matters to us — if you have a moment, we\'d really appreciate your feedback.</p>' }),
            createBlock('text', {
                bgColor: '#f5f3ff',
                html: `<table width="100%" cellpadding="0" cellspacing="0" style="font-family:Arial,sans-serif;font-size:13px">
<tr><td style="padding:5px 0;color:#7c3aed;width:130px">Ticket ID</td><td style="padding:5px 0;font-weight:700;color:#2d3748">{{ticketId}}</td></tr>
<tr><td style="padding:5px 0;color:#7c3aed">Form</td><td style="padding:5px 0;color:#374151">{{formTitle}}</td></tr>
<tr><td style="padding:5px 0;color:#7c3aed">Status</td><td style="padding:5px 0;font-weight:700;color:#7c3aed">Resolved</td></tr>
</table>`,
                paddingTop: 16, paddingBottom: 16, paddingLeft: 28, paddingRight: 28,
            }),
            createBlock('button', { text: 'View Ticket', url: '{{ticketURL}}', bgColor: '#7c3aed', paddingTop: 20, paddingBottom: 4 }),
            createBlock('button', { text: 'Submit Feedback', url: '{{formURL}}', bgColor: '#ffffff', color: '#7c3aed', borderRadius: 6, containerBg: '#ffffff', paddingTop: 8, paddingBottom: 8 }),
            createBlock('text', { html: '<p style="margin:0;color:#718096;font-size:12px;text-align:center">Not satisfied? Reopen your ticket from the portal and we\'ll take another look.</p>' }),
            createBlock('divider', {}),
            createBlock('footer', {}),
        ],
    },

    // ════════════════════════════════════════════════════════════════════════
    //  STATUS — RECEIVED / OPEN  (variantIds 101-112)
    // ════════════════════════════════════════════════════════════════════════
    {
        key: 'status_received_professional',
        channelType: 'status_received',
        label: 'Professional',
        tone: 'Formal',
        accent: '#2784B9',
        desc: 'Clean blue acknowledgement for new or open tickets.',
        make: () => [
            createBlock('header', { title: 'Ticket Received', subtitle: 'Your request has been logged', bgColor: '#2784B9' }),
            createBlock('text', { html: '<p style="margin:0 0 12px">Dear <strong>{{toUserFullName}}</strong>,</p><p style="margin:0">Thank you for reaching out. Your support ticket has been successfully created and our team has been notified. We will review your request and respond as soon as possible.</p>' }),
            createBlock('divider', {}),
            createBlock('text', {
                ...infoTable([['Ticket ID', '{{ticketId}}'], ['Form', '{{formTitle}}'], ['Status', '{{ticketStatus}}'],], '#f0f7ff'),
                paddingTop: 16, paddingBottom: 16, paddingLeft: 28, paddingRight: 28,
            }),
            createBlock('button', { text: 'Track Your Ticket', url: '{{ticketURL}}', bgColor: '#2784B9', paddingTop: 24, paddingBottom: 8 }),
            createBlock('text', { html: '<p style="margin:0;color:#718096;font-size:13px">Questions? Simply reply to this email and we\'ll be happy to help.</p>' }),
            createBlock('divider', {}),
            createBlock('footer', {}),
        ],
    },
    {
        key: 'status_received_friendly',
        channelType: 'status_received',
        label: 'Friendly',
        tone: 'Warm',
        accent: '#0891b2',
        desc: 'Approachable, welcoming tone for customer-facing teams.',
        make: () => [
            createBlock('header', { title: 'We\'ve got it!', subtitle: 'Your ticket is in good hands', bgColor: '#0891b2', paddingTop: 36, paddingBottom: 36 }),
            createBlock('text', { html: '<p style="margin:0 0 14px">Hey <strong>{{toUserFullName}}</strong>,</p><p style="margin:0 0 12px">We\'ve received your request and our team will get on it shortly. Your reference number is below — keep it handy for any follow-ups.</p>' }),
            createBlock('text', {
                bgColor: '#ecfeff',
                html: '<p style="margin:0;font-size:13px;color:#0e7490">Reference: <strong style="font-size:16px;color:#0891b2">{{ticketId}}</strong></p>',
                paddingTop: 14, paddingBottom: 14, paddingLeft: 20, paddingRight: 20,
            }),
            createBlock('button', { text: 'View Your Ticket', url: '{{ticketURL}}', bgColor: '#0891b2', borderRadius: 24, paddingTop: 20, paddingBottom: 12 }),
            createBlock('divider', {}),
            createBlock('footer', { bgColor: '#f0fdff' }),
        ],
    },
    {
        key: 'status_received_minimal',
        channelType: 'status_received',
        label: 'Minimal',
        tone: 'Clean',
        accent: '#374151',
        desc: 'No-frills confirmation — just the reference and a link.',
        make: () => [
            createBlock('header', { title: 'Ticket Created', subtitle: '', bgColor: '#1f2937', paddingTop: 28, paddingBottom: 28 }),
            createBlock('text', { html: '<p style="margin:0 0 16px;color:#374151">Hello <strong>{{toUserFullName}}</strong>,</p><p style="margin:0;color:#6b7280;line-height:1.7">Your ticket <strong>{{ticketId}}</strong> has been received. We\'ll review it and follow up shortly. Use the link below to check the current status at any time.</p>' }),
            createBlock('button', { text: 'View Ticket', url: '{{ticketURL}}', bgColor: '#1f2937', borderRadius: 4, paddingTop: 20, paddingBottom: 8 }),
            createBlock('divider', { borderColor: '#e5e7eb' }),
            createBlock('footer', { bgColor: '#f9fafb', html: '<p style="color:#9ca3af;font-size:11px;text-align:center;margin:0">Automated notification · <a href="{{ticketURL}}" style="color:#9ca3af">{{ticketId}}</a></p>' }),
        ],
    },

    // ════════════════════════════════════════════════════════════════════════
    //  STATUS — IN PROGRESS  (variantIds 201-214)
    // ════════════════════════════════════════════════════════════════════════
    {
        key: 'status_inprogress_standard',
        channelType: 'status_inprogress',
        label: 'In Progress',
        tone: 'Informative',
        accent: '#d97706',
        desc: 'Amber update — clear and informative for active tickets.',
        make: () => [
            createBlock('header', { title: 'Ticket In Progress', subtitle: 'Our team is working on your request', bgColor: '#d97706' }),
            createBlock('text', { html: '<p style="margin:0 0 12px">Hi <strong>{{toUserFullName}}</strong>,</p><p style="margin:0">We wanted to let you know that your ticket is now being actively worked on. We\'ll keep you updated as we make progress and will reach out if we need any additional information from you.</p>' }),
            createBlock('divider', {}),
            createBlock('text', {
                ...infoTable([['Ticket ID', '{{ticketId}}'], ['Status', '{{ticketStatus}}', '#d97706'], ['Form', '{{formTitle}}'],], '#fffbeb'),
                paddingTop: 16, paddingBottom: 16, paddingLeft: 28, paddingRight: 28,
            }),
            createBlock('button', { text: 'View Latest Update', url: '{{ticketURL}}', bgColor: '#d97706', paddingTop: 24, paddingBottom: 8 }),
            createBlock('divider', {}),
            createBlock('footer', {}),
        ],
    },
    {
        key: 'status_inprogress_action',
        channelType: 'status_inprogress',
        label: 'Action Required',
        tone: 'Urgent',
        accent: '#dc2626',
        desc: 'Red accent prompting the submitter to respond or provide information.',
        make: () => [
            createBlock('header', { title: 'Your Input Needed', subtitle: 'Please respond to keep your ticket moving', bgColor: '#dc2626' }),
            createBlock('text', { html: '<p style="margin:0 0 12px">Hi <strong>{{toUserFullName}}</strong>,</p><p style="margin:0">We\'re working on ticket <strong>{{ticketId}}</strong> and need some additional information from you before we can continue. Please visit your ticket and provide the details requested.</p>' }),
            createBlock('text', {
                bgColor: '#fff1f2',
                html: '<p style="margin:0;font-size:13px;color:#dc2626;font-weight:600">Without your input, we may not be able to progress this ticket.</p>',
                paddingTop: 14, paddingBottom: 14, paddingLeft: 20, paddingRight: 20,
            }),
            createBlock('button', { text: 'Respond Now', url: '{{ticketURL}}', bgColor: '#dc2626', borderRadius: 6, paddingTop: 20, paddingBottom: 8 }),
            createBlock('text', { html: '<p style="margin:0;color:#718096;font-size:13px">Already responded? You can ignore this email.</p>' }),
            createBlock('divider', {}),
            createBlock('footer', {}),
        ],
    },
    {
        key: 'status_inprogress_pending',
        channelType: 'status_inprogress',
        label: 'Pending Approval',
        tone: 'Neutral',
        accent: '#7c3aed',
        desc: 'Purple — highlights that the ticket is awaiting a decision.',
        make: () => [
            createBlock('header', { title: 'Awaiting Approval', subtitle: 'Your request is pending a decision', bgColor: '#7c3aed' }),
            createBlock('text', { html: '<p style="margin:0 0 12px">Hi <strong>{{toUserFullName}}</strong>,</p><p style="margin:0">Your ticket <strong>{{ticketId}}</strong> is currently pending approval. Once a decision has been made, we will notify you immediately. No action is needed from you at this time.</p>' }),
            createBlock('text', {
                bgColor: '#f5f3ff',
                html: `<p style="margin:0;text-align:center;font-family:Arial,sans-serif"><span style="font-size:12px;color:#7c3aed;text-transform:uppercase;letter-spacing:1px;font-weight:700">Current Status</span><br><span style="font-size:22px;font-weight:700;color:#5b21b6">{{ticketStatus}}</span></p>`,
                paddingTop: 24, paddingBottom: 24, paddingLeft: 28, paddingRight: 28,
            }),
            createBlock('button', { text: 'View Ticket', url: '{{ticketURL}}', bgColor: '#7c3aed', borderRadius: 8, paddingTop: 20, paddingBottom: 8 }),
            createBlock('divider', {}),
            createBlock('footer', {}),
        ],
    },

    // ════════════════════════════════════════════════════════════════════════
    //  STATUS — RESOLVED / CLOSED  (variantIds 301-318)
    // ════════════════════════════════════════════════════════════════════════
    {
        key: 'status_resolved_professional',
        channelType: 'status_resolved',
        label: 'Professional',
        tone: 'Formal',
        accent: '#059669',
        desc: 'Classic green resolution email with a full summary.',
        make: () => [
            createBlock('header', { title: 'Ticket Resolved', subtitle: 'Your request has been completed', bgColor: '#059669' }),
            createBlock('text', { html: '<p style="margin:0 0 12px">Dear <strong>{{toUserFullName}}</strong>,</p><p style="margin:0">We are pleased to inform you that your support ticket has been resolved. If you believe the issue has not been fully addressed, you can reopen your ticket at any time using the portal link below.</p>' }),
            createBlock('divider', {}),
            createBlock('text', {
                ...infoTable([['Ticket ID', '{{ticketId}}'], ['Status', '{{ticketStatus}}', '#059669'], ['Form', '{{formTitle}}'], ['Contact', '{{toUserEmail}}'],], '#ecfdf5'),
                paddingTop: 16, paddingBottom: 16, paddingLeft: 28, paddingRight: 28,
            }),
            createBlock('button', { text: 'View Resolution', url: '{{ticketURL}}', bgColor: '#059669', paddingTop: 24, paddingBottom: 8 }),
            createBlock('text', { html: '<p style="margin:0;color:#718096;font-size:13px">Thank you for giving us the opportunity to assist you.</p>' }),
            createBlock('divider', {}),
            createBlock('footer', {}),
        ],
    },
    {
        key: 'status_resolved_positive',
        channelType: 'status_resolved',
        label: 'Great News',
        tone: 'Celebratory',
        accent: '#10b981',
        desc: 'Upbeat, positive tone — makes closure feel like a win.',
        make: () => [
            createBlock('header', { title: 'All Done!', subtitle: 'Your issue has been resolved', bgColor: '#10b981', paddingTop: 40, paddingBottom: 40 }),
            createBlock('text', { html: '<p style="margin:0 0 14px">Hi <strong>{{toUserFullName}}</strong>,</p><p style="margin:0 0 12px">Great news — your ticket has been resolved! Our team worked hard to get this sorted for you and we hope everything is back to normal.</p><p style="margin:0">If everything looks good on your end, no further action is needed.</p>' }),
            createBlock('text', {
                bgColor: '#f0fdf4',
                html: '<p style="margin:0;text-align:center;color:#059669;font-size:13px">Ticket <strong>{{ticketId}}</strong> is now marked as <strong>{{ticketStatus}}</strong></p>',
                paddingTop: 16, paddingBottom: 16, paddingLeft: 20, paddingRight: 20,
            }),
            createBlock('button', { text: 'View Your Ticket', url: '{{ticketURL}}', bgColor: '#10b981', borderRadius: 24, paddingTop: 20, paddingBottom: 8 }),
            createBlock('text', { html: '<p style="margin:0;color:#718096;font-size:13px;text-align:center">Something still not right? You can reopen your ticket from the portal anytime.</p>' }),
            createBlock('divider', {}),
            createBlock('footer', { bgColor: '#f0fdf4' }),
        ],
    },
    {
        key: 'status_resolved_declined',
        channelType: 'status_resolved',
        label: 'Declined / Closed',
        tone: 'Formal',
        accent: '#64748b',
        desc: 'Neutral tone for declined, cancelled, or withdrawn tickets.',
        make: () => [
            createBlock('header', { title: 'Ticket Closed', subtitle: 'Reference: {{ticketId}}', bgColor: '#475569', paddingTop: 32, paddingBottom: 32 }),
            createBlock('text', { html: '<p style="margin:0 0 12px">Dear <strong>{{toUserFullName}}</strong>,</p><p style="margin:0">Your ticket <strong>{{ticketId}}</strong> has been closed with status <strong>{{ticketStatus}}</strong>. If you have questions about this outcome or need to raise a new request, please do not hesitate to contact us.</p>' }),
            createBlock('text', {
                bgColor: '#f8fafc',
                html: `<table width="100%" cellpadding="0" cellspacing="0" style="font-family:Arial,sans-serif;font-size:13px"><tr><td style="padding:6px 0;color:#64748b;width:130px">Ticket ID</td><td style="padding:6px 0;color:#1e293b;font-weight:700">{{ticketId}}</td></tr><tr><td style="padding:6px 0;color:#64748b">Form</td><td style="padding:6px 0;color:#334155">{{formTitle}}</td></tr><tr><td style="padding:6px 0;color:#64748b">Outcome</td><td style="padding:6px 0;color:#334155">{{ticketStatus}}</td></tr></table>`,
                paddingTop: 16, paddingBottom: 16, paddingLeft: 20, paddingRight: 20,
            }),
            createBlock('button', { text: 'View Ticket Details', url: '{{ticketURL}}', bgColor: '#475569', borderRadius: 4, paddingTop: 20, paddingBottom: 8 }),
            createBlock('divider', { borderColor: '#e2e8f0' }),
            createBlock('footer', { bgColor: '#f8fafc' }),
        ],
    },

    // ════════════════════════════════════════════════════════════════════════
    //  STATUS — REOPENED  (variantIds 401-412)
    // ════════════════════════════════════════════════════════════════════════
    {
        key: 'status_reopened_standard',
        channelType: 'status_reopened',
        label: 'Reopened',
        tone: 'Reassuring',
        accent: '#ea580c',
        desc: 'Orange — acknowledges the reopen and reassures the submitter.',
        make: () => [
            createBlock('header', { title: 'Ticket Reopened', subtitle: 'We\'re back on it', bgColor: '#ea580c' }),
            createBlock('text', { html: '<p style="margin:0 0 12px">Hi <strong>{{toUserFullName}}</strong>,</p><p style="margin:0">Your ticket <strong>{{ticketId}}</strong> has been reopened. Our team has been notified and will pick it up shortly. We are committed to getting this fully resolved for you.</p>' }),
            createBlock('divider', {}),
            createBlock('text', {
                ...infoTable([['Ticket ID', '{{ticketId}}'], ['Status', '{{ticketStatus}}', '#ea580c'], ['Form', '{{formTitle}}'],], '#fff7ed'),
                paddingTop: 16, paddingBottom: 16, paddingLeft: 28, paddingRight: 28,
            }),
            createBlock('button', { text: 'View Your Ticket', url: '{{ticketURL}}', bgColor: '#ea580c', paddingTop: 24, paddingBottom: 8 }),
            createBlock('text', { html: '<p style="margin:0;color:#718096;font-size:13px">Need to add more context? Visit your ticket and add a comment.</p>' }),
            createBlock('divider', {}),
            createBlock('footer', {}),
        ],
    },
    {
        key: 'status_reopened_followup',
        channelType: 'status_reopened',
        label: 'Follow-Up',
        tone: 'Proactive',
        accent: '#b45309',
        desc: 'Acknowledges a follow-up or ongoing concern with a warm tone.',
        make: () => [
            createBlock('header', { title: 'Follow-Up Opened', subtitle: 'We noticed your concern continues', bgColor: '#b45309', paddingTop: 36, paddingBottom: 36 }),
            createBlock('text', { html: '<p style="margin:0 0 12px">Hi <strong>{{toUserFullName}}</strong>,</p><p style="margin:0 0 12px">We\'ve noted that your issue from ticket <strong>{{ticketId}}</strong> is ongoing. A follow-up has been opened and a team member will be in touch with you shortly to ensure this is fully resolved.</p><p style="margin:0;color:#6b7280;font-size:13px">Current status: <strong>{{ticketStatus}}</strong></p>' }),
            createBlock('button', { text: 'View Follow-Up Ticket', url: '{{ticketURL}}', bgColor: '#b45309', borderRadius: 6, paddingTop: 20, paddingBottom: 8 }),
            createBlock('divider', {}),
            createBlock('footer', {}),
        ],
    },

    // ════════════════════════════════════════════════════════════════════════
    //  STATUS — ESCALATED  (variantIds 501-514)
    // ════════════════════════════════════════════════════════════════════════
    {
        key: 'status_escalated_priority',
        channelType: 'status_escalated',
        label: 'Priority Escalation',
        tone: 'Urgent',
        accent: '#dc2626',
        desc: 'Red — signals urgency and priority handling to the submitter.',
        make: () => [
            createBlock('header', { title: 'Ticket Escalated', subtitle: 'Your ticket is receiving priority attention', bgColor: '#dc2626' }),
            createBlock('text', { html: '<p style="margin:0 0 12px">Hi <strong>{{toUserFullName}}</strong>,</p><p style="margin:0">Your ticket <strong>{{ticketId}}</strong> has been escalated to our senior team. We take your concern very seriously and you will receive priority attention from this point forward. A dedicated team member will be in touch with you shortly.</p>' }),
            createBlock('text', {
                bgColor: '#fff1f2',
                html: '<p style="margin:0;font-size:13px;color:#dc2626;font-weight:600">Escalated — Priority handling is now active on your ticket.</p>',
                paddingTop: 14, paddingBottom: 14, paddingLeft: 20, paddingRight: 20,
            }),
            createBlock('text', {
                ...infoTable([['Ticket ID', '{{ticketId}}'], ['Status', '{{ticketStatus}}', '#dc2626'], ['Form', '{{formTitle}}'],], '#fef2f2'),
                paddingTop: 16, paddingBottom: 16, paddingLeft: 28, paddingRight: 28,
            }),
            createBlock('button', { text: 'View Your Ticket', url: '{{ticketURL}}', bgColor: '#dc2626', paddingTop: 20, paddingBottom: 8 }),
            createBlock('divider', {}),
            createBlock('footer', {}),
        ],
    },
    {
        key: 'status_escalated_formal',
        channelType: 'status_escalated',
        label: 'Formal Escalation',
        tone: 'Corporate',
        accent: '#1e3a5f',
        desc: 'Dark navy — formal escalation for enterprise or compliance use.',
        make: () => [
            createBlock('header', { title: 'Formal Escalation Notice', subtitle: 'Reference: {{ticketId}}', bgColor: '#1e3a5f', paddingTop: 36, paddingBottom: 36 }),
            createBlock('text', { html: '<p style="margin:0 0 12px">Dear <strong>{{toUserFullName}}</strong>,</p><p style="margin:0">We are writing to inform you that your ticket <strong>{{ticketId}}</strong> has been formally escalated. Your case has been assigned to a senior team member who will review the matter in full and respond within the agreed timeframe. Please retain this reference for all future correspondence.</p>' }),
            createBlock('text', {
                bgColor: '#f1f5f9',
                html: `<table width="100%" cellpadding="0" cellspacing="0" style="font-family:Arial,sans-serif;font-size:13px"><tr><td style="padding:6px 0;color:#64748b;width:140px">Reference</td><td style="padding:6px 0;font-weight:700;color:#1e293b;font-size:15px">{{ticketId}}</td></tr><tr><td style="padding:6px 0;color:#64748b">Submitted Via</td><td style="padding:6px 0;color:#334155">{{formTitle}}</td></tr><tr><td style="padding:6px 0;color:#64748b">Current Status</td><td style="padding:6px 0;color:#334155">{{ticketStatus}}</td></tr><tr><td style="padding:6px 0;color:#64748b">Contact</td><td style="padding:6px 0;color:#334155">{{toUserEmail}}</td></tr></table>`,
                paddingTop: 16, paddingBottom: 16, paddingLeft: 20, paddingRight: 20,
            }),
            createBlock('button', { text: 'Access Ticket Portal', url: '{{ticketURL}}', bgColor: '#1e3a5f', borderRadius: 4, paddingTop: 20, paddingBottom: 8 }),
            createBlock('divider', { borderColor: '#e2e8f0' }),
            createBlock('footer', { bgColor: '#f8fafc', html: '<p style="color:#94a3b8;font-size:11px;text-align:center;margin:0">This is a system-generated notification. For assistance, visit your ticket portal.</p>' }),
        ],
    },
    {
        key: 'status_escalated_specialist',
        channelType: 'status_escalated',
        label: 'Specialist Referral',
        tone: 'Reassuring',
        accent: '#7c3aed',
        desc: 'Purple — reassures the submitter that a specialist is now involved.',
        make: () => [
            createBlock('header', { title: 'Referred to a Specialist', subtitle: 'Expert attention is on its way', bgColor: '#7c3aed', paddingTop: 36, paddingBottom: 36 }),
            createBlock('text', { html: '<p style="margin:0 0 12px">Hi <strong>{{toUserFullName}}</strong>,</p><p style="margin:0 0 12px">Your ticket <strong>{{ticketId}}</strong> has been referred to a specialist who has the expertise to address your specific concern. You can expect a detailed and considered response from them shortly.</p><p style="margin:0;color:#6b7280;font-size:13px">Status: <strong>{{ticketStatus}}</strong></p>' }),
            createBlock('button', { text: 'View Ticket', url: '{{ticketURL}}', bgColor: '#7c3aed', borderRadius: 8, paddingTop: 20, paddingBottom: 8 }),
            createBlock('text', { html: '<p style="margin:0;color:#718096;font-size:13px;text-align:center">You will receive a notification as soon as there is an update on your ticket.</p>' }),
            createBlock('divider', {}),
            createBlock('footer', {}),
        ],
    },
];

// ── Quill config ─────────────────────────────────────────────────────────────
const QUILL_MODULES = {
    toolbar: [
        [{ header: [1, 2, false] }],
        ['bold', 'italic', 'underline'],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        ['link'],
        ['clean'],
    ],
};
const QUILL_FORMATS = ['header', 'bold', 'italic', 'underline', 'color', 'background', 'align', 'link'];

// ── Block visual preview ──────────────────────────────────────────────────────
function BlockPreview({ block }) {
    if (block.type === 'header') {
        return (
            <div style={{ background: block.bgColor, padding: `${block.paddingTop}px ${block.paddingRight}px ${block.paddingBottom}px ${block.paddingLeft}px`, textAlign: 'center', fontFamily: 'Arial, sans-serif' }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.65)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>{block.logoText}</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: block.titleColor, lineHeight: 1.2, marginBottom: block.subtitle ? 6 : 0 }}>{block.title}</div>
                {block.subtitle && <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.82)' }}>{block.subtitle}</div>}
            </div>
        );
    }
    if (block.type === 'text' || block.type === 'footer') {
        return (
            <div style={{ background: block.bgColor, padding: `${block.paddingTop}px ${block.paddingRight}px ${block.paddingBottom}px ${block.paddingLeft}px`, fontSize: 13, color: '#333', lineHeight: 1.6, fontFamily: 'Arial, sans-serif' }}
                dangerouslySetInnerHTML={{ __html: block.html }} />
        );
    }
    if (block.type === 'button') {
        return (
            <div style={{ background: block.containerBg ?? '#fff', padding: `${block.paddingTop}px ${block.paddingRight}px ${block.paddingBottom}px ${block.paddingLeft}px`, textAlign: block.align }}>
                <span style={{ display: 'inline-block', background: block.bgColor, color: block.color, padding: '11px 26px', borderRadius: block.borderRadius, fontWeight: 700, fontSize: 14, fontFamily: 'Arial, sans-serif' }}>{block.text}</span>
            </div>
        );
    }
    if (block.type === 'divider') {
        return (
            <div style={{ background: block.bgColor, padding: `${block.paddingTop}px ${block.paddingRight}px ${block.paddingBottom}px ${block.paddingLeft}px` }}>
                <hr style={{ border: 'none', borderTop: `1px solid ${block.borderColor}`, margin: 0 }} />
            </div>
        );
    }
    if (block.type === 'spacer') {
        return <div style={{ background: block.bgColor, height: block.height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: 10, color: '#ccc' }}>Spacer {block.height}px</span>
        </div>;
    }
    return null;
}

// ── Scaled mini preview for template cards ────────────────────────────────────
function TemplateMiniPreview({ blocks }) {
    return (
        <div className={`${cn}__miniPreview`}>
            <div className={`${cn}__miniPreview__inner`}>
                {blocks.slice(0, 5).map(b => <BlockPreview key={b.id} block={b} />)}
            </div>
        </div>
    );
}

// ── Block style panel ─────────────────────────────────────────────────────────
function BlockEditor({ block, onChange, variables }) {
    const quillRef = useRef(null);
    const u = useCallback((key, val) => onChange({ ...block, [key]: val }), [block, onChange]);

    const insertVar = useCallback((varKey) => {
        const token = `{{${varKey}}}`;
        if ((block.type === 'text' || block.type === 'footer') && quillRef.current) {
            const quill = quillRef.current.getEditor();
            const range = quill.getSelection(true) ?? { index: quill.getLength() - 1 };
            quill.insertText(range.index, token, 'user');
            quill.setSelection(range.index + token.length, 0);
            quill.focus();
        } else if (block.type === 'button') {
            u('url', token);
        } else if (block.type === 'header') {
            u('title', block.title + ' ' + token);
        }
    }, [block, u]);

    return (
        <div className={`${cn}__editor`}>
            {['text', 'footer', 'button', 'header'].includes(block.type) && (
                <div className={`${cn}__editor__vars`}>
                    <span className={`${cn}__editor__vars__label`}>Insert variable:</span>
                    <div className={`${cn}__editor__vars__chips`}>
                        {variables.map(v => (
                            <button key={v.key} type="button" className={`${cn}__editor__vars__chip`} title={v.desc} onClick={() => insertVar(v.key)}>{v.label}</button>
                        ))}
                    </div>
                </div>
            )}

            <div className={`${cn}__editor__body`}>
                {block.type === 'header' && (
                    <div className={`${cn}__editor__fields`}>
                        <Field label="Logo Text"><input type="text" value={block.logoText} onChange={e => u('logoText', e.target.value)} /></Field>
                        <Field label="Title"><input type="text" value={block.title} onChange={e => u('title', e.target.value)} /></Field>
                        <Field label="Subtitle"><input type="text" value={block.subtitle} onChange={e => u('subtitle', e.target.value)} placeholder="Optional" /></Field>
                        <Field label="Background"><ColorField value={block.bgColor} onChange={v => u('bgColor', v)} /></Field>
                        <Field label="Title Color"><ColorField value={block.titleColor} onChange={v => u('titleColor', v)} /></Field>
                    </div>
                )}
                {(block.type === 'text' || block.type === 'footer') && (
                    <div className={`${cn}__editor__fields`}>
                        <div className={`${cn}__editor__quillWrap`}>
                            <ReactQuill ref={quillRef} theme="snow" modules={QUILL_MODULES} formats={QUILL_FORMATS} value={block.html} onChange={val => u('html', val)} />
                        </div>
                        <Field label="Background"><ColorField value={block.bgColor} onChange={v => u('bgColor', v)} /></Field>
                    </div>
                )}
                {block.type === 'button' && (
                    <div className={`${cn}__editor__fields`}>
                        <Field label="Button Text"><input type="text" value={block.text} onChange={e => u('text', e.target.value)} /></Field>
                        <Field label="URL / Variable"><input type="text" value={block.url} onChange={e => u('url', e.target.value)} /></Field>
                        <Field label="Alignment">
                            <select value={block.align} onChange={e => u('align', e.target.value)}>
                                <option value="left">Left</option>
                                <option value="center">Center</option>
                                <option value="right">Right</option>
                            </select>
                        </Field>
                        <Field label="Button Color"><ColorField value={block.bgColor} onChange={v => u('bgColor', v)} /></Field>
                        <Field label="Text Color"><ColorField value={block.color} onChange={v => u('color', v)} /></Field>
                        <Field label="Container BG"><ColorField value={block.containerBg ?? '#ffffff'} onChange={v => u('containerBg', v)} /></Field>
                        <Field label="Border Radius"><input type="number" value={block.borderRadius} min={0} max={50} onChange={e => u('borderRadius', +e.target.value || 0)} /></Field>
                    </div>
                )}
                {block.type === 'divider' && (
                    <div className={`${cn}__editor__fields`}>
                        <Field label="Line Color"><ColorField value={block.borderColor} onChange={v => u('borderColor', v)} /></Field>
                        <Field label="Background"><ColorField value={block.bgColor} onChange={v => u('bgColor', v)} /></Field>
                    </div>
                )}
                {block.type === 'spacer' && (
                    <div className={`${cn}__editor__fields`}>
                        <Field label="Height (px)"><input type="number" value={block.height} min={4} max={120} onChange={e => u('height', +e.target.value || 8)} /></Field>
                        <Field label="Background"><ColorField value={block.bgColor} onChange={v => u('bgColor', v)} /></Field>
                    </div>
                )}
                {block.type !== 'spacer' && (
                    <div className={`${cn}__editor__padding`}>
                        <span className={`${cn}__editor__padding__label`}>Padding</span>
                        {[['paddingTop', 'T'], ['paddingRight', 'R'], ['paddingBottom', 'B'], ['paddingLeft', 'L']].map(([k, lbl]) => (
                            <div key={k} className={`${cn}__editor__padding__field`}>
                                <input type="number" value={block[k] ?? 16} min={0} max={80} onChange={e => u(k, +e.target.value || 0)} />
                                <span>{lbl}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div className={`${cn}__field`}>
            <label className={`${cn}__field__label`}>{label}</label>
            {children}
        </div>
    );
}

function ColorField({ value, onChange }) {
    return (
        <div className={`${cn}__colorField`}>
            <input type="color" value={value} onChange={e => onChange(e.target.value)} />
            <span>{value}</span>
        </div>
    );
}

// ── Template gallery modal ────────────────────────────────────────────────────
const CHANNEL_LABELS = {
    create:            'On Ticket Create',
    update:            'On Ticket Update',
    resolve:           'On Ticket Resolve',
    status_received:   'Status — Received / Open',
    status_inprogress: 'Status — In Progress',
    status_resolved:   'Status — Resolved / Closed',
    status_reopened:   'Status — Reopened',
    status_escalated:  'Status — Escalated',
};

function TemplateModal({ channelType, onSelect, onClose }) {
    const templates = TEMPLATES.filter(t => t.channelType === channelType);

    return (
        <div className={`${cn}__modal`}>
            <div className={`${cn}__modal__overlay`} onClick={onClose} />
            <div className={`${cn}__modal__dialog ${cn}__modal__dialog--templates`}>
                <div className={`${cn}__modal__header`}>
                    <div className={`${cn}__modal__header__text`}>
                        <span className={`${cn}__modal__header__title`}>Email Templates</span>
                        <span className={`${cn}__modal__header__sub`}>{CHANNEL_LABELS[channelType]}</span>
                    </div>
                    <button className={`${cn}__modal__close`} onClick={onClose}><CloseIcon /></button>
                </div>

                <p className={`${cn}__modal__hint`}>
                    Choose a starting point. You can edit all text, colors, and layout after selecting.
                </p>

                <div className={`${cn}__templateGrid`}>
                    {templates.map(tpl => {
                        const previewBlocks = tpl.make();
                        return (
                            <button key={tpl.key} className={`${cn}__templateCard`} onClick={() => { onSelect(previewBlocks); onClose(); }}>
                                <div className={`${cn}__templateCard__accent`} style={{ background: tpl.accent }} />
                                <TemplateMiniPreview blocks={previewBlocks} />
                                <div className={`${cn}__templateCard__info`}>
                                    <div className={`${cn}__templateCard__top`}>
                                        <span className={`${cn}__templateCard__label`}>{tpl.label}</span>
                                        <span className={`${cn}__templateCard__tone`} style={{ background: tpl.accent + '22', color: tpl.accent }}>{tpl.tone}</span>
                                    </div>
                                    <div className={`${cn}__templateCard__desc`}>{tpl.desc}</div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

// ── Preview modal ─────────────────────────────────────────────────────────────
function PreviewModal({ html, onClose }) {
    return (
        <div className={`${cn}__modal`}>
            <div className={`${cn}__modal__overlay`} onClick={onClose} />
            <div className={`${cn}__modal__dialog ${cn}__modal__dialog--preview`}>
                <div className={`${cn}__modal__header`}>
                    <span>Email Preview</span>
                    <button className={`${cn}__modal__close`} onClick={onClose}><CloseIcon /></button>
                </div>
                <div className={`${cn}__previewFrame`}>
                    <iframe title="Email Preview" srcDoc={html} style={{ width: '100%', height: '100%', border: 'none' }} sandbox="allow-same-origin" />
                </div>
            </div>
        </div>
    );
}

// ── Block type menu ───────────────────────────────────────────────────────────
export const BLOCK_TYPES = [
    { type: 'header', label: 'Header', desc: 'Branded header with color & logo' },
    { type: 'text', label: 'Text', desc: 'Rich text paragraph' },
    { type: 'button', label: 'Button', desc: 'Call-to-action button' },
    { type: 'divider', label: 'Divider', desc: 'Horizontal separator line' },
    { type: 'spacer', label: 'Spacer', desc: 'Empty vertical space' },
    { type: 'footer', label: 'Footer', desc: 'Small footer with fine print' },
];

// ── EmailDesigner ─────────────────────────────────────────────────────────────
const EmailDesigner = forwardRef(function EmailDesigner({ value, onChange, variables = [], channelType = 'create', sidebarMode = false }, ref) {
    const [blocks, setBlocks] = useState(() => {
        const parsed = parseDesignerData(value);
        if (parsed) return parsed.blocks ?? [];
        if (value && value.trim()) return [createBlock('text', { html: value })];
        return [];
    });
    const [canvasBg, setCanvasBg] = useState(() => parseDesignerData(value)?.canvasBg ?? '#f0f4f8');
    const [selectedId, setSelectedId] = useState(null);
    const [showTemplates, setShowTemplates] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [showAddMenu, setShowAddMenu] = useState(false);

    const html = useMemo(() => exportHtml(blocks, canvasBg), [blocks, canvasBg]);

    const prevRef = useRef(null);
    useEffect(() => {
        if (html !== prevRef.current) { prevRef.current = html; onChange(html); }
    }, [html, onChange]);

    const updateBlock = useCallback((id, updated) =>
        setBlocks(prev => prev.map(b => b.id === id ? { ...b, ...updated } : b)), []);

    const deleteBlock = useCallback((id) => {
        setBlocks(prev => prev.filter(b => b.id !== id));
        setSelectedId(s => s === id ? null : s);
    }, []);

    const moveBlock = useCallback((id, dir) => {
        setBlocks(prev => {
            const idx = prev.findIndex(b => b.id === id);
            const to = idx + dir;
            if (idx < 0 || to < 0 || to >= prev.length) return prev;
            const arr = [...prev];
            [arr[idx], arr[to]] = [arr[to], arr[idx]];
            return arr;
        });
    }, []);

    const addBlock = useCallback((type) => {
        const b = createBlock(type);
        setBlocks(prev => [...prev, b]);
        setSelectedId(b.id);
        setShowAddMenu(false);
    }, []);

    const applyTemplate = useCallback((tplBlocks) => {
        setBlocks(tplBlocks);
        setSelectedId(null);
    }, []);

    const applyTheme = useCallback((headerColor, accentColor) => {
        setBlocks(prev => prev.map(b => {
            if (b.type === 'header') return { ...b, bgColor: headerColor };
            if (b.type === 'button') return { ...b, bgColor: accentColor };
            return b;
        }));
    }, []);

    useImperativeHandle(ref, () => ({ addBlock, applyTemplate, applyTheme }), [addBlock, applyTemplate, applyTheme]);

    return (
        <div className={cn}>
            {/* Toolbar */}
            <div className={`${cn}__toolbar`}>
                {!sidebarMode && (
                    <button className={`${cn}__toolbar__btn ${cn}__toolbar__btn--accent`} type="button" onClick={() => setShowTemplates(true)}>
                        <TemplatesIcon /> Templates
                    </button>
                )}
                <button className={`${cn}__toolbar__btn`} type="button" onClick={() => setShowPreview(true)}>
                    <PreviewIcon /> Preview
                </button>
                {!sidebarMode && (
                    <div className={`${cn}__toolbar__addWrap`}>
                        <button className={`${cn}__toolbar__btn`} type="button" onClick={() => setShowAddMenu(v => !v)}>
                            <AddIcon /> Add Block
                        </button>
                        {showAddMenu && (
                            <>
                                <div className={`${cn}__addMenu__overlay`} onClick={() => setShowAddMenu(false)} />
                                <div className={`${cn}__addMenu`}>
                                    {BLOCK_TYPES.map(({ type, label, desc }) => (
                                        <button key={type} type="button" className={`${cn}__addMenu__item`} onClick={() => addBlock(type)}>
                                            <span className={`${cn}__addMenu__item__label`}>{label}</span>
                                            <span className={`${cn}__addMenu__item__desc`}>{desc}</span>
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                )}
                <div className={`${cn}__toolbar__canvas`}>
                    <label>Canvas BG</label>
                    <input type="color" value={canvasBg} onChange={e => setCanvasBg(e.target.value)} title="Canvas background color" />
                </div>
            </div>

            {/* Canvas */}
            <div className={`${cn}__canvas`} style={{ background: canvasBg }}>
                {blocks.length === 0 ? (
                    <div className={`${cn}__empty`}>
                        <MailIcon className={`${cn}__empty__icon`} />
                        <p className={`${cn}__empty__text`}>Start by choosing a template, or add blocks manually.</p>
                        <button className={`${cn}__empty__btn`} type="button" onClick={() => setShowTemplates(true)}>
                            Browse Templates
                        </button>
                    </div>
                ) : (
                    <div className={`${cn}__emailWrap`}>
                        {blocks.map((block, idx) => {
                            const isSelected = selectedId === block.id;
                            return (
                                <div key={block.id} className={`${cn}__block ${isSelected ? `${cn}__block--selected` : ''}`}>
                                    <div className={`${cn}__block__bar`}>
                                        <span className={`${cn}__block__bar__type`}>{block.type}</span>
                                        <div className={`${cn}__block__bar__actions`}>
                                            <button type="button" title="Move up" disabled={idx === 0} onClick={() => moveBlock(block.id, -1)}><UpIcon /></button>
                                            <button type="button" title="Move down" disabled={idx === blocks.length - 1} onClick={() => moveBlock(block.id, 1)}><DownIcon /></button>
                                            <button type="button" className={`${cn}__block__bar__edit`} onClick={() => setSelectedId(isSelected ? null : block.id)}>
                                                {isSelected ? <><ExpandLessIcon /> Done</> : <><ExpandMoreIcon /> Edit</>}
                                            </button>
                                            <button type="button" className={`${cn}__block__bar__delete`} onClick={() => deleteBlock(block.id)}><DeleteIcon /></button>
                                        </div>
                                    </div>
                                    <div className={`${cn}__block__preview`} onClick={() => !isSelected && setSelectedId(block.id)}>
                                        <BlockPreview block={block} />
                                    </div>
                                    {isSelected && (
                                        <BlockEditor block={block} onChange={updated => updateBlock(block.id, updated)} variables={variables} />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {showTemplates && <TemplateModal channelType={channelType} onSelect={applyTemplate} onClose={() => setShowTemplates(false)} />}
            {showPreview && <PreviewModal html={html} onClose={() => setShowPreview(false)} />}
        </div>
    );
});

export default EmailDesigner;
